// Single case study pages (Figma "desktop-case-study-single").
// One entry per case study — each gets its own address: /new-site/case-studies/<slug>.
// To edit a case study, change its entry below; to add one, add a new entry.
import previsicoFloodAsset from "@/assets/new-site/previsico-flood-culvert.jpg.asset.json";
import crowdguardNottsForestAsset from "@/assets/new-site/crowdguard-notts-forest.jpg.asset.json";
import koriImg1 from "@/assets/kori-img1.jpg";
import koriImg2 from "@/assets/kori-img2.jpg";
import nationalHighwaysCulvertAsset from "@/assets/new-site/national-highways-culvert.jpg.asset.json";
import kentCountyCouncilSignAsset from "@/assets/new-site/kent-county-council-sign.jpg.asset.json";
import kentCountyCouncilRoadworksAsset from "@/assets/new-site/kent-county-council-roadworks.jpg.asset.json";
import previsicoNetworkRailAsset from "@/assets/new-site/previsico-network-rail-culvert.webp.asset.json";
import crowdguardBarriersAsset from "@/assets/new-site/crowdguard-hvm-barriers.png.asset.json";
const nationalHighwaysCulvert = nationalHighwaysCulvertAsset.url;
const kentCountyCouncilSign = kentCountyCouncilSignAsset.url;
const kentCountyCouncilRoadworks = kentCountyCouncilRoadworksAsset.url;
const previsicoNetworkRail = previsicoNetworkRailAsset.url;
const crowdguardBarriers = crowdguardBarriersAsset.url;
const crowdguardNottsForest = crowdguardNottsForestAsset.url;
const previsicoFlood = previsicoFloodAsset.url;

import { caseStudies } from "./caseStudies";

export type CaseStudyTopic = "Resilience" | "Streetworks" | "Construction";

export interface ArticleSection {
  heading: string;
  /** Paragraphs shown under the heading. */
  paragraphs?: string[];
  /** Optional bullet list shown after the paragraphs. */
  bullets?: string[];
}

export interface CaseStudyArticle {
  slug: string;
  title: string;
  /** Card summary on the overview page. */
  summary: string;
  /** Grey introduction line under the title. */
  intro: string;
  date: string;
  topic: CaseStudyTopic;
  heroImage: string;
  /** Where the wide banner crop sits on the hero photo, e.g. "center 25%". Defaults to centred. */
  heroPosition?: string;
  inlineImage?: { src: string; caption: string; alt: string };
  /** Sections before the inline image (usually one). */
  sectionsBeforeImage: ArticleSection[];
  sections: ArticleSection[];
  /** Large closing statement at the end of the article. */
  closing?: string;
}

const previsico = caseStudies.find((s) => s.id === "previsico")!;
const kori = caseStudies.find((s) => s.id === "kori")!;
const quoteSection = (q: { text: string; attribution: string }[]): ArticleSection[] =>
  q.map((x) => ({ heading: x.attribution, paragraphs: x.text.split("\n\n").map((p, i) => (i === 0 ? `“${p}` : p)).map((p, i, a) => (i === a.length - 1 ? `${p}”` : p)) }));

export const caseStudyArticles: CaseStudyArticle[] = [
  {
    slug: "kent-county-council",
    title: "Kent County Council: Smarter Remote Monitoring of Streetworks",
    summary: "Snapshot Live provides KCC with a low-cost, remote way to see what is happening on site and act when it matters.",
    intro: "Snapshot Live cameras remotely monitor selected streetworks sites, giving inspectors a clear, time-stamped visual record of activity and progress.",
    date: "Sep 28, 2026",
    topic: "Streetworks",
    heroImage: kentCountyCouncilRoadworks,
    heroPosition: "center 40%",
    inlineImage: { src: kentCountyCouncilSign, caption: "A Snapshot Live monitoring sign marking a streetworks site operated by Kent County Council", alt: "A yellow Snapshot Live monitoring sign mounted on a post, with a banner reading Operated by Kent County Council" },
    sectionsBeforeImage: [
      { heading: "The Challenge", paragraphs: ["Kent County Council (KCC) manages hundreds of streetworks permits each month across more than 30 utility companies. With limited resources, physically inspecting every live site is not practical, making it harder to monitor progress, compliance, safety and permit overruns."] },
    ],
    sections: [
      { heading: "The Snapshot Live Solution", paragraphs: ["KCC is using Snapshot Live cameras to remotely monitor selected streetworks sites, providing a clear, time-stamped visual record of activity and progress.", "Typically deployed in groups to provide full site coverage, Snapshot enables KCC inspectors to quickly identify issues including signage, lighting and guarding breaches, unsafe working practices and potential permit overruns.", "This gives inspectors better visibility of works without requiring repeated site visits and allows them to intervene quickly where concerns are identified."] },

      {
        heading: "The Impact",
        paragraphs: ["Snapshot Live is helping KCC to:"],
        bullets: [
          "Reduce unnecessary permit extension requests and overruns",
          "Improve compliance with agreed working hours and permit conditions",
          "Identify signage, lighting, guarding and safety issues earlier",
          "Reduce the need for physical site inspections",
          "Encourage better behaviour and accountability from utility contractors",
          "Reduce the time streetworks occupy the road network",
        ],
      },
      { heading: "The Result", paragraphs: ["By combining remote visibility with targeted physical inspections, KCC can manage streetworks more efficiently, improve site compliance and safety, and reduce disruption to road users."] },
      ...quoteSection([
        {
          text: "Snapshot Live provided us with complete visibility of the works site on a strategically important part of the network. The time-stamped imagery was used to verify site presence, monitor progression of the works, and ensure compliance with site set up and permit conditions. The camera is straightforward to install and modify as and where required.",
          attribution: "Ashley Bailey, Senior Streetworks Coordinator, Kent County Council",
        },
      ]),
    ],
    closing: "Snapshot Live provides KCC with a low-cost, remote way to see what is happening on site and act when it matters.",
  },
  {
    slug: "crowdguard",
    title: "CrowdGuard: New Layer of Assurance to Hostile Vehicle Mitigation",
    summary: "Snapshot Live gives CrowdGuard a simple, privacy-first way to strengthen assurance around temporary HVM deployments while keeping inspection and operational decisions with competent personnel.",
    intro: "Snapshot Live gives CrowdGuard a simple, privacy-first way to strengthen assurance around temporary HVM deployments.",
    date: "Sep 28, 2026",
    topic: "Construction",
    heroImage: crowdguardBarriers,
    heroPosition: "center 50%",
    inlineImage: { src: crowdguardNottsForest, caption: "Temporary HVM barriers deployed outside Nottingham Forest's ground", alt: "A row of red and white mobile vehicle barriers parked on asphalt outside a football stadium stand with exposed red steel roof trusses" },
    sectionsBeforeImage: [
      { heading: "The Challenge", paragraphs: ["Temporary Hostile Vehicle Mitigation (HVM) deployments can involve multiple teams, tight installation windows and several access points. While competent personnel remain responsible for installation, inspection and sign-off, creating a consistent record of what was in place, and whether anything changed can be difficult."] },
    ],
    sections: [
      { heading: "The Snapshot Live Solution", paragraphs: ["Working with CrowdGuard, Snapshot Live provides an additional visual assurance layer for temporary HVM deployments.", "Snapshot captures scheduled, time-stamped images during installation, at handover and throughout an event. This creates a clear visual audit trail without continuous CCTV.", "AI-assisted image analysis can also identify potential movement, absence or misalignment of HVM equipment and flag it for review by the appropriate team."] },
      {
        heading: "The Impact",
        paragraphs: ["Snapshot Live helps CrowdGuard and its customers to:"],
        bullets: [
          "Provide time-stamped evidence of HVM deployments",
          "Strengthen installation and handover assurance",
          "Identify potential movement or misalignment",
          "Target physical inspections where they are most needed",
          "Create a clear audit trail for post-event review",
          "Deploy rapidly without fixed power or connectivity",
        ],
      },
      { heading: "The Result", paragraphs: ["Snapshot Live gives CrowdGuard a simple, privacy-first way to strengthen assurance around temporary HVM deployments while keeping inspection and operational decisions with competent personnel. The solution is now being installed near sports venues across the UK including Manchester United, Nottingham Forest, Leeds United and Newcastle United."] },
      ...quoteSection([
        {
          text: "For safety officers, assurance matters. Snapshot Live helps support that by providing a practical, non-intrusive way to evidence that agreed HVM measures appear to be in place at key stages of a live operation. It is about strengthening visibility, accountability and confidence in matchday environments and crowded spaces.",
          attribution: "Deborah Ainscough, Founder of Crowdguard",
        },
      ]),
    ],
    closing: "From “we believe it was in place” to “we can evidence what was there, and when.”",
  },
  {
    slug: "previsico",
    title: "Previsico: Adding Visual Intelligence to Flood Forecasting",
    summary: "Prediction tells you what might happen. Sensors tell you something is happening. Snapshot lets you see it so that you can proactively maintain safety critical assets.",
    intro: "Prediction tells you what might happen. Sensors tell you something is happening. Snapshot lets you see it.",
    date: "Sep 28, 2026",
    topic: "Resilience",
    heroImage: previsicoNetworkRail,
    heroPosition: "center 70%",
    inlineImage: { src: previsicoFlood, caption: "Floodwater at a railed culvert inlet captured by Snapshot Live", alt: "Floodwater standing in a roadside ditch beside a railed concrete culvert inlet, captured by Snapshot Live" },
    sectionsBeforeImage: [
      {
        heading: "The Challenge",
        paragraphs: [
          "Previsico helps organisations understand and respond to flood risk through forecasting, hydrological monitoring and real-time flood intelligence. But sensors typically measure conditions at a specific point. They cannot always show the wider context such as whether debris is blocking a culvert, vegetation is affecting a sensor, how far water has spread, or what condition an asset is in following a flood.",
        ],
      },
    ],
    sections: [
      {
        heading: "The Snapshot Live Solution",
        paragraphs: [
          "Snapshot Live adds an autonomous “eye on the ground” alongside Previsico’s forecasting and sensor data.",
          "Snapshot cameras can be rapidly deployed at flood hotspots, culverts, drainage assets and other vulnerable locations.",
          "Images are captured routinely during normal conditions, with frequency increasing automatically as flood risk develops. This gives teams visual confirmation before, during and after an event without requiring constant physical inspection.",
        ],
      },
      {
        heading: "The Impact",
        paragraphs: ["Combining Previsico and Snapshot Live helps organisations to:"],
        bullets: [
          "Identify debris, vegetation and emerging blockages earlier",
          "Visually confirm rising water levels and wider flood impact",
          "Target preventative maintenance before severe weather",
          "Reduce unnecessary site visits",
          "Improve situational awareness during flood events",
          "Assess damage and maintenance needs remotely",
          "Build a visual record of changing site conditions",
        ],
      },
      {
        heading: "The Result",
        paragraphs: [
          "The combined solution is supporting major infrastructure organisations including National Grid, Network Rail and National Highways, giving teams greater visibility of vulnerable and difficult-to-access locations. By combining forecasting, sensors and autonomous imagery, these organisations can build a clearer picture of what is happening on the ground and make faster, better-informed decisions.",
        ],
      },
      ...quoteSection(previsico.quotes),
    ],
    closing: "Prediction tells you what might happen. Sensors tell you something is happening. Snapshot lets you see it so that you can proactively maintain safety critical assets.",
  },
  {
    slug: "kori-construction",
    title: "Kori Construction: Improving Site Safety, Community Impact & Operational Oversight",
    summary: "Portable, AI-assisted cameras give Kori proactive, data-driven oversight of site safety and community impact.",
    intro: "Portable, AI-assisted cameras give Kori proactive, data-driven oversight of site safety and community impact.",
    date: "Sep 28, 2026",
    topic: "Construction",
    heroImage: koriImg1,
    inlineImage: { src: koriImg2, caption: "Site access point monitored by Snapshot Live", alt: "Construction site access point monitored by Snapshot Live" },
    sectionsBeforeImage: [{ heading: "The Challenge", paragraphs: kori.body.slice(0, 2) }],
    sections: [
      { heading: "The Snapshot Live Solution", paragraphs: kori.body.slice(2) },
      { heading: "The Impact", bullets: (kori.benefits ?? []).map((b) => `${b.title}: ${b.description}`) },
      ...quoteSection(kori.quotes),
    ],
    closing: kori.conclusion,
  },
  {
    slug: "national-highways",
    title: "National Highways: Proactive Maintenance & 66% Fewer Inspections",
    summary: "Time-stamped imagery of high-risk structures helps National Highways cut inspections by 66% and clear assets before flooding develops.",
    intro: "Proactive maintenance of drainage structures with 66% fewer inspections.",
    date: "Sep 28, 2026",
    topic: "Resilience",
    heroImage: nationalHighwaysCulvert,
    inlineImage: {
      src: nationalHighwaysCulvert,
      caption: "A Snapshot Live camera overlooking a culvert at a high-risk structure",
      alt: "A Snapshot Live camera mounted on a post overlooking a flooded culvert",
    },
    sectionsBeforeImage: [
      {
        heading: "The Challenge",
        paragraphs: [
          "National Highways Area 7 manages high-risk structures including culverts and bridges that can require inspection following significant flood events.",
          "Without clear evidence of what had happened at a structure, engineers could be required to attend site even where critical water levels had not been reached. These inspections are costly, time-consuming and can require traffic management, specialist surveys and engineering review.",
        ],
      },
    ],
    sections: [
      {
        heading: "The Snapshot Live Solution",
        paragraphs: [
          "Snapshot Live cameras were deployed at high-risk structures to provide time-stamped imagery of water levels with AI analysing for debris, blockages and the wider condition of each asset. Used alongside water-level monitoring and flood forecasting from Previsico, the imagery gives engineers the visual evidence needed to decide whether a physical inspection is actually required.",
          "Crucially, Snapshot also lets teams identify debris and potential obstructions ahead of forecast rainfall, enabling preventative clearance before conditions deteriorate.",
        ],
      },
      {
        heading: "The Impact",
        paragraphs: ["The solution is helping National Highways to:"],
        bullets: [
          "Reduce inspection frequency by 66% and save an estimated £19,000+ per year in inspection costs across just 6 structures",
          "Remotely assess asset condition before deploying engineers",
          "Identify debris and obstruction risks earlier",
          "Target preventative clearance ahead of severe weather",
          "Reduce unnecessary engineering reviews and site visits",
          "Build a visual record of how structures behave during flood events",
        ],
      },
      {
        heading: "The Result",
        paragraphs: [
          "Snapshot Live has helped National Highways move from reactive inspection towards more preventative, intelligence-led asset management. By seeing what is happening at vulnerable structures remotely, teams can intervene earlier, clear assets before flooding develops and focus inspections only where the evidence shows they are needed.",
        ],
      },
      ...quoteSection([
        {
          text: "The sensors and cameras have allowed us to carry out preventative debris clearance from our structures and reduced the risk of future flooding significantly.",
          attribution: "Andrew Simpson, Structures Engineer, National Highways",
        },
      ]),
    ],
    closing: "Snapshot Live has helped National Highways move from reactive inspection towards more preventative, intelligence-led asset management.",
  },
];

export const findCaseStudyArticle = (slug?: string) => caseStudyArticles.find((a) => a.slug === slug);
